import React, { FC, useContext } from 'react';

import styled from '@emotion/styled';
import { ThemeProps, variant } from '@lucid/theme';

import { NavbarDropdownContext } from './navbar-dropdown';

export type NavbarDropdownContainerVariants = 'default';

export interface NavbarDropdownContainerProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks if the NavbarDropdown should be displayed on the left. */
  displayLeft?: boolean;
  /** Checks if the NavbarDropdown is open. */
  isOpen?: boolean;
  /** Checks if the mobile toggle button should be displayed. */
  mobileToggle?: boolean;
  /** The theme object to apply styles to the NavbarDropdownContainer component. */
  theme?: ThemeProps['theme'];
  /** The available variants for the NavbarDropdownContainer component. */
  variant?: NavbarDropdownContainerVariants;
}

const StyledDropdownContainer = styled.div<NavbarDropdownContainerProps>(
  variant({
    scale: 'navbar.dropdownContainer',
    variants: {
      defaultVariant: {}
    }
  }),
  ({
    theme,
    displayLeft,
    isOpen,
    mobileToggle = false
  }) => ({
    label: 'navbarDropdownContainer',
    display: 'flex',
    flexWrap: 'wrap',
    visibility: isOpen ? 'visible' : 'hidden',
    position: 'absolute',
    textAlign: 'left',
    minWidth: '225px',
    maxWidth: '300px',
    width: '100%',
    zIndex: theme.zIndices.z1500,
    right: displayLeft ? undefined : 0,
    left: displayLeft ? 0 : undefined,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: '-27px',
      height: '27px',
      maxWidth: '80%'
    },
    [`@media (max-width: ${theme.navbar.collapseAt})`]: [
      mobileToggle && { transition: 'height ease-out 0.3s' },
      {
        top: '100%',
        visibility: mobileToggle ? (isOpen ? 'visible' : 'hidden') : 'visible',
        height: mobileToggle ? (isOpen ? 'auto' : 0) : 'auto',
        maxWidth: '100%',
        padding: 0,
        position: 'relative',
        border: 'none',
        flexWrap: 'wrap',
        '&::before': {
          display: 'none'
        }
      }
    ]
  })
);

const NavbarDropdownContainer: FC<NavbarDropdownContainerProps> = ({
  children,
  className,
  variant = 'default',
  ...rest
}) => {
  const { isOpen, displayLeft } = useContext(NavbarDropdownContext);

  return (
    <StyledDropdownContainer
      {...rest}
      className={className}
      displayLeft={displayLeft}
      isOpen={isOpen}
      variant={variant}
    >
      {children}
    </StyledDropdownContainer>
  );
};

export { NavbarDropdownContainer };
export default NavbarDropdownContainer;
